import { t } from '../i18n';
import { callTabCoordination } from '../services/callTabCoordination';
import { useCallStore } from '../store/callStore';
import { showToast } from '../utils/toast';

type CreateIncomingCallUiEffectsParams = {
  closeWindowIncomingCallNotification: (callSessionId: string) => void;
  postToServiceWorker: (message: any) => void;
  playRingtone: () => void;
  stopRingtone: () => void;
  clearIncomingCallStaleTimer: () => void;
  markCallEndedInHistory: (params: {
    callSessionId: string;
    activeCall?: import('../store/callStore').ActiveCall | null;
    incomingCall?: import('../store/callStore').IncomingCall | null;
    reason?: string;
    source?: 'ws' | 'webrtc' | 'user' | 'security' | 'unknown';
    durationSeconds?: number;
  }) => void;
  handleCallEnd: (info?: { reason?: string; source?: 'ws' | 'webrtc' | 'user' | 'security' | 'unknown' }) => void;
};

export function showSecurityToast(messageKey: string) {
  const raw = (t(messageKey) || '').toString();
  const firstLine = raw.split('\n')[0]?.trim();
  const message = firstLine || raw.trim() || 'Security verification failed';
  showToast({ message, variant: 'error', durationMs: 10000 });
}

export function isLocalIncomingCallLeader(callSessionId: string | null | undefined): boolean {
  return callTabCoordination.getSnapshot(callSessionId).isLeader;
}

export function createIncomingCallUiEffects(params: CreateIncomingCallUiEffectsParams) {
  return {
    applyIncomingLeadershipState(callSessionId: string) {
      const state = useCallStore.getState();
      const incoming = state.incomingCall;
      if (!incoming || incoming.callSessionId !== callSessionId) return;

      if (isLocalIncomingCallLeader(callSessionId)) {
        maybeShowIncomingCallNotification(incoming.fromDisplayName || t('common.unknown'), callSessionId);
        params.playRingtone();
        return;
      }

      params.stopRingtone();
      params.closeWindowIncomingCallNotification(callSessionId);
      params.postToServiceWorker({ type: 'CLOSE_CALL_NOTIFICATION', callSessionId });
    },

    handleRemoteTabCallAction(callSessionId: string, action: 'accepted' | 'rejected' | 'ended') {
      const state = useCallStore.getState();
      const incoming = state.incomingCall;
      const activeCall = state.activeCall;

      if (incoming?.callSessionId === callSessionId) {
        params.clearIncomingCallStaleTimer();
        params.closeWindowIncomingCallNotification(callSessionId);
        params.postToServiceWorker({ type: 'CLOSE_CALL_NOTIFICATION', callSessionId });
        params.stopRingtone();

        params.markCallEndedInHistory({
          callSessionId,
          incomingCall: incoming,
          ...(action === 'rejected' ? { reason: 'tab_rejected' } : {})
        });

        state.setIncomingCall(null);
      }

      if (activeCall?.callSessionId === callSessionId) {
        const startedAtMs = activeCall.startedAt?.getTime() || 0;
        const answeredMomentsAgo = !activeCall.isInitiator
          && startedAtMs > 0
          && Date.now() - startedAtMs < 10_000;

        if (action === 'ended' && answeredMomentsAgo) {
          return;
        }

        params.handleCallEnd({ reason: `tab_${action}`, source: 'unknown' });
      }
    }
  };
}

export type IncomingCallUiEffects = ReturnType<typeof createIncomingCallUiEffects>;

const windowIncomingCallNotifications = new Map<string, Notification>();

export function closeWindowIncomingCallNotification(callSessionId: string) {
  try {
    const id = String(callSessionId || '').trim();
    if (!id) return;

    const notification = windowIncomingCallNotifications.get(id);
    if (notification) {
      try {
        notification.close();
      } catch {
        // ignore
      }
      windowIncomingCallNotifications.delete(id);
    }
  } catch {
    // ignore
  }
}

function maybeShowIncomingCallNotification(fromDisplayName: string, callSessionId: string) {
  try {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    if (document.visibilityState === 'visible') return;

    closeWindowIncomingCallNotification(callSessionId);

    const notification = new Notification(t('incomingCall.notificationTitle'), {
      body: fromDisplayName
        ? t('incomingCall.notificationFrom', { name: fromDisplayName })
        : t('incomingCall.notificationTitle'),
      tag: `call-${callSessionId}`,
      requireInteraction: true,
      icon: '/circlus-favicon-dark.svg'
    });
    notification.onclick = () => {
      try {
        window.focus();
      } catch {
        // ignore
      }

      try {
        if (window.location.pathname !== '/calls' && window.location.pathname !== '/call') {
          window.history.pushState({}, '', '/calls');
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      } catch {
        // ignore
      }

      try {
        notification.close();
      } catch {
        // ignore
      }
    };

    windowIncomingCallNotifications.set(String(callSessionId), notification);
  } catch {
    // ignore
  }
}
